import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, Search, AlertCircle } from 'lucide-react'
import {
    useFindStore,
    SEARCH_PHASES,
} from '../../stores/useFindStore'
import SearchInput from '.'

const Wrapper = ({ children }) => (
    // `position: relative; z-index: 2` lifts the empty state above the
    // legacy `#graph-container` div (z-index 1) the EJS shell still
    // renders globally. Without it, this sits under an invisible overlay.
    <div
        className="project-notes-scope"
        style={{
            position: 'relative',
            zIndex: 2,
            minHeight: '60vh',
        }}
    >
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="
                tw-flex tw-flex-col tw-items-center tw-justify-center
                tw-text-center
                tw-px-6
                tw-text-neutral-600 dark:tw-text-neutral-400
            "
            style={{ minHeight: '60vh' }}
        >
            {children}
        </motion.div>
    </div>
)

const IdleState = () => (
    <Wrapper>
        <Search className="tw-w-10 tw-h-10 tw-mb-3 tw-text-neutral-300 dark:tw-text-neutral-600" />
        <h2 className="tw-text-base tw-font-semibold tw-mb-1 tw-text-neutral-700 dark:tw-text-neutral-200">
            Search your graphs by concept
        </h2>
        <p className="tw-text-sm tw-max-w-md tw-mb-5">
            Type a concept and press Enter. Add more terms to narrow results.
        </p>
        {/*
         * Shared `layoutId` with the SearchHeader's slot. When the user
         * submits a term, the AppBar mounts its own copy of this motion.div
         * and framer-motion animates the input up into the toolbar.
         *
         * The wrapper is sized to the SearchInput pill's max width (480px)
         * so the `items-center` on the parent flex-col actually centers the
         * visible pill, not a wider invisible container around it.
         */}
        <motion.div
            layoutId="graph-search-input"
            transition={{
                type: 'spring',
                stiffness: 320,
                damping: 32,
            }}
            style={{
                width: '100%',
                maxWidth: 480,
            }}
        >
            <SearchInput />
        </motion.div>
    </Wrapper>
)

const LoadingState = () => (
    <Wrapper>
        <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-neutral-400 dark:tw-text-neutral-500" />
        <p className="tw-text-sm tw-mt-3">Searching…</p>
    </Wrapper>
)

const EmptyState = ({ terms }) => (
    <Wrapper>
        <Search className="tw-w-10 tw-h-10 tw-mb-3 tw-text-neutral-300 dark:tw-text-neutral-600" />
        <h2 className="tw-text-base tw-font-semibold tw-mb-1 tw-text-neutral-700 dark:tw-text-neutral-200">
            No matching statements
        </h2>
        <p className="tw-text-sm tw-max-w-md">
            Nothing in your graphs contains all of:{' '}
            <span className="tw-font-medium">
                {terms.map(t => `"${t}"`).join(', ')}
            </span>
            . Try removing a term to broaden the search.
        </p>
    </Wrapper>
)

const ErrorState = ({ message, onRetry }) => (
    <Wrapper>
        <AlertCircle className="tw-w-10 tw-h-10 tw-mb-3 tw-text-red-400 dark:tw-text-red-500" />
        <h2 className="tw-text-base tw-font-semibold tw-mb-1 tw-text-neutral-700 dark:tw-text-neutral-200">
            Search failed
        </h2>
        <p className="tw-text-sm tw-max-w-md tw-mb-3">{message}</p>
        <button
            type="button"
            onClick={onRetry}
            className="
                tw-text-sm tw-font-medium
                tw-text-blue-600 dark:tw-text-blue-400
                hover:tw-underline
                tw-underline-offset-2
            "
        >
            Try again
        </button>
    </Wrapper>
)

export default function SearchEmptyStates() {
    const searchPhase = useFindStore(s => s.searchPhase)
    const terms = useFindStore(s => s.terms)
    const lastError = useFindStore(s => s.lastError)
    const retry = useFindStore(s => s.retry)

    return (
        <AnimatePresence mode="wait">
            {searchPhase === SEARCH_PHASES.IDLE && <IdleState key="idle" />}
            {searchPhase === SEARCH_PHASES.LOADING && (
                <LoadingState key="loading" />
            )}
            {searchPhase === SEARCH_PHASES.EMPTY && (
                <EmptyState key="empty" terms={terms} />
            )}
            {searchPhase === SEARCH_PHASES.ERROR && (
                <ErrorState
                    key="error"
                    message={lastError || 'Something went wrong.'}
                    onRetry={retry}
                />
            )}
        </AnimatePresence>
    )
}
